import { css } from '@emotion/react'
import { ConsultaSeguimentoIndicatorHelper } from '@mchat-rf/consulta-seguimento/components/ConsultaSeguimentoIndicatorHelper'
import { ConsultaSeguimentoIndicatorResultado } from '@mchat-rf/consulta-seguimento/components/ConsultaSeguimentoIndicatorResultado'
import { ConsultaSeguimentoIndicatorStart } from '@mchat-rf/consulta-seguimento/components/ConsultaSeguimentoIndicatorStart'
import { ConsultaSeguimentoIndicatorStep } from '@mchat-rf/consulta-seguimento/components/ConsultaSeguimentoIndicatorStep'
import { ConsultaSeguimentoPerguntaHeader } from '@mchat-rf/consulta-seguimento/components/ConsultaSeguimentoPerguntaHeader'
import { ConsultaSeguimentoPerguntaHeaderTitle } from '@mchat-rf/consulta-seguimento/components/ConsultaSeguimentoPerguntaHeaderTitle'
import {
  ExemplosHorizontalField,
  ExemplosHorizontalFieldOptions,
} from '@mchat-rf/consulta-seguimento/components/ExemplosHorizontalField'
import { isPerguntaStatusRespondendo } from '@mchat-rf/consulta-seguimento/consulta-seguimento-aplicacao/utils-consultaSeguimentoAplicacao'
import { PassouFalhouEnum } from '@mchat-rf/consulta-seguimento/enums-consultaSeguimento'
import { useConsultaSeguimentoAutoScroll } from '@mchat-rf/consulta-seguimento/hooks/useConsultaSeguimentoAutoScroll'
import { useConsultaSeguimentoSetResultado } from '@mchat-rf/consulta-seguimento/hooks/useConsultaSeguimentoSetResultado'
import { Heading, Text, VFlow } from 'bold-ui'
import { resolveName, SimNaoEnum } from 'components/form'
import { SimNaoRadioGroupBoxField } from 'components/form/field/SimNaoRadioGroupBoxField'
import { SimNaoSwitchButtonField } from 'components/form/field/SimNaoSwitchButtonField'
import { MChatPerguntaEnum } from 'graphql/types.generated'
import { RefObject } from 'react'
import { useField } from 'react-final-form'
import { MetaPath } from 'util/metaPath'

import {
  ConsultaSeguimentoPergunta05ExemplosEnum,
  ConsultaSeguimentoPergunta05Model,
} from './model-consultaSeguimentoPergunta05'
import { PERGUNTA_05_SCROLL_CONFIG } from './scrollConfig-consultaSeguimentoPergunta05'
import {
  calculateResultadoPergunta05,
  conditionToCalculatePergunta05,
  hasPergunta05ExemplosFalhouAnySim,
  hasPergunta05ExemplosPassouAnySim,
  isPergunta05ExemplosFalhouAllFilled,
  isPergunta05ExemplosFalhouAllNao,
  isPergunta05ExemplosPassouAllNao,
} from './utils-consultaSeguimentoPergunta05'

interface ConsultaSeguimentoPergunta05FieldProps {
  name: MetaPath<ConsultaSeguimentoPergunta05Model>
  containerRef: RefObject<HTMLDivElement>
  goNextButtonRef: RefObject<HTMLButtonElement>
}

export function ConsultaSeguimentoPergunta05Field(props: Readonly<ConsultaSeguimentoPergunta05FieldProps>) {
  const { name, containerRef, goNextButtonRef } = props

  const {
    input: { value },
  } = useField<ConsultaSeguimentoPergunta05Model>(resolveName(name), {
    subscription: { value: true },
  })

  useConsultaSeguimentoSetResultado<ConsultaSeguimentoPergunta05Model>({
    name,
    conditionToCalculate: conditionToCalculatePergunta05,
    calculateResultado: calculateResultadoPergunta05,
  })

  const { getId, triggerScrollFor } = useConsultaSeguimentoAutoScroll<ConsultaSeguimentoPergunta05Model>({
    name,
    containerRef,
    goNextButtonRef,
    scrollConfig: PERGUNTA_05_SCROLL_CONFIG,
  })

  const hasAnySimExemplosPassou = hasPergunta05ExemplosPassouAnySim(value.PERGUNTA_05_EXEMPLOS)
  const hasAnySimExemplosFalhou = hasPergunta05ExemplosFalhouAnySim(value)
  const isExemplosPassouAllNao = isPergunta05ExemplosPassouAllNao(value.PERGUNTA_05_EXEMPLOS)
  const isExemplosFalhouAllNao = isPergunta05ExemplosFalhouAllNao(value)
  const isExemplosFalhouAllFilled = isPergunta05ExemplosFalhouAllFilled(value)
  const isAnySimPassouAllNaoFalhou = hasAnySimExemplosPassou && isExemplosFalhouAllNao
  const isAllExemplosNao = isExemplosPassouAllNao && isExemplosFalhouAllNao
  const isExemplosPassouFilled = isExemplosPassouAllNao || hasAnySimExemplosPassou

  const isExemplosFilled = isExemplosPassouFilled && isExemplosFalhouAllFilled

  const finalizouNaPerguntaExemplos =
    value.PERGUNTA_05_HEADER === SimNaoEnum.SIM && (isAnySimPassouAllNaoFalhou || isAllExemplosNao)

  const textResultadoAnySimPassouAllNaoFalhou =
    value.PERGUNTA_05_HEADER === SimNaoEnum.SIM && isAnySimPassouAllNaoFalhou
      ? TEXT_RESULTADO_ANY_SIM_PASSOU_ALL_NAO_FALHOU
      : undefined

  const textResultadoAllExemplosNao =
    value.PERGUNTA_05_HEADER === SimNaoEnum.SIM && isAllExemplosNao ? TEXT_RESULTADO_ALL_EXEMPLOS_NAO : undefined

  const textResultadoPassou = textResultadoAnySimPassouAllNaoFalhou || textResultadoAllExemplosNao

  return (
    <VFlow vSpacing={0}>
      <VFlow>
        {isPerguntaStatusRespondendo(value.status) && (
          <ConsultaSeguimentoPerguntaHeader
            pergunta={MChatPerguntaEnum.PERGUNTA_05}
            title={
              <ConsultaSeguimentoPerguntaHeaderTitle sufix=' faz movimentos estranhos com os dedos perto dos olhos dele?' />
            }
            status={value.status}
            field={
              <SimNaoSwitchButtonField
                name={name.PERGUNTA_05_HEADER}
                onChange={() => triggerScrollFor('PERGUNTA_05_HEADER')}
              />
            }
          />
        )}
        {value.PERGUNTA_05_HEADER === SimNaoEnum.SIM && (
          <VFlow vSpacing={0}>
            <VFlow id={getId('PERGUNTA_05_EXEMPLOS')}>
              <VFlow vSpacing={0}>
                <ConsultaSeguimentoIndicatorStart />
                <ConsultaSeguimentoIndicatorHelper
                  title='Por favor, descreva esses movimentos.'
                  subtitle='(Se o genitor não der um dos exemplos abaixo, perguntar cada um deles individualmente.)'
                />
              </VFlow>
              <VFlow vSpacing={0}>
                <ExemplosHorizontalField
                  name={name.PERGUNTA_05_EXEMPLOS}
                  nameOutro={name.OUTRO}
                  label='Responda os exemplos Passou e Falhou até pontuar na Pergunta 5 ou avançar para o próximo fluxo '
                  options={PERGUNTA_05_EXEMPLOS_OPTIONS}
                  showIndicator={isExemplosFilled && hasAnySimExemplosFalhou ? PassouFalhouEnum.FALHOU : undefined}
                  showTagOptional={{ PASSOU: hasAnySimExemplosPassou }}
                  onChange={() => triggerScrollFor('PERGUNTA_05_EXEMPLOS')}
                />
                {isExemplosFilled && hasAnySimExemplosFalhou && (
                  <VFlow vSpacing={0} id={getId('PERGUNTA_05_DUAS_VEZES_POR_SEMANA')}>
                    <ConsultaSeguimentoIndicatorStep
                      hideConnector
                      text={
                        <VFlow vSpacing={0}>
                          <Text color='inherit'>Sim para qualquer exemplo Falhou:</Text>
                          <Heading level={3} color='inherit'>
                            Pergunte com que frequência estes exemplos com “Sim” ocorrem
                          </Heading>
                        </VFlow>
                      }
                      style={styles.indicatorStep}
                    />
                    <SimNaoRadioGroupBoxField
                      name={name.PERGUNTA_05_DUAS_VEZES_POR_SEMANA}
                      label='Isso acontece mais de duas vezes por semana?'
                      onChange={() => triggerScrollFor('PERGUNTA_05_DUAS_VEZES_POR_SEMANA')}
                      required
                    />
                  </VFlow>
                )}
              </VFlow>
            </VFlow>
          </VFlow>
        )}
      </VFlow>
      {value.resultado && (
        <ConsultaSeguimentoIndicatorResultado
          resultado={value.resultado}
          text={{ PASSOU: textResultadoPassou }}
          connectorVariant={finalizouNaPerguntaExemplos ? 'horizontal' : 'default'}
          style={styles.indicatorResultado}
        />
      )}
    </VFlow>
  )
}

const TEXT_RESULTADO_ANY_SIM_PASSOU_ALL_NAO_FALHOU =
  'Sim para pelo menos um exemplo Passou e Não para todos exemplos Falhou:'

const TEXT_RESULTADO_ALL_EXEMPLOS_NAO = 'Não para todos exemplos Falhou:'

const PERGUNTA_05_EXEMPLOS_OPTIONS: ExemplosHorizontalFieldOptions<ConsultaSeguimentoPergunta05ExemplosEnum> = {
  PASSOU: [
    {
      label: 'Olha para as mãos?',
      value: ConsultaSeguimentoPergunta05ExemplosEnum.OLHA_PARA_AS_MAOS,
    },
    {
      label: 'Move os dedos quando brinca de mostrar e esconder o rosto?',
      value: ConsultaSeguimentoPergunta05ExemplosEnum.MOVE_OS_DEDOS_QUANDO_BRINCA_DE_ESCONDER_ROSTO,
    },
  ],
  FALHOU: [
    {
      label: 'Movimenta os dedos perto dos olhos?',
      value: ConsultaSeguimentoPergunta05ExemplosEnum.MOVIMENTA_OS_DEDOS_PERTO_DOS_OLHOS,
    },
    {
      label: 'Mantém as mãos elevadas perto dos olhos?',
      value: ConsultaSeguimentoPergunta05ExemplosEnum.MANTEM_AS_MAOS_ELEVADAS_PERTO_DOS_OLHOS,
    },
    {
      label: 'Mantém as mãos ao lado dos olhos?',
      value: ConsultaSeguimentoPergunta05ExemplosEnum.MANTEM_AS_MAOS_AO_LADO_DOS_OLHOS,
    },
    {
      label: 'Agita as mãos perto do rosto?',
      value: ConsultaSeguimentoPergunta05ExemplosEnum.AGITA_AS_MAOS_PERTO_DO_ROSTO,
    },
  ],
}

const styles = {
  indicatorStep: css`
    width: auto;
  `,
  indicatorResultado: css`
    max-width: 18rem;
  `,
}
